import { useState, useId, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { useSession } from '@/store/useSession'
import styles from './LoginPage.module.css'

const PRACTITIONER_EMAIL    = 'practitioner@fitutogether.com'
const PRACTITIONER_PASSWORD = 'FitU2026!'

function isValidPractitionerLogin(email: string, password: string): boolean {
  return (
    email.trim().toLowerCase() === PRACTITIONER_EMAIL &&
    password === PRACTITIONER_PASSWORD
  )
}

export default function LoginPage() {
  const { palette } = useTheme()
  const navigate = useNavigate()
  const { setUserRole } = useSession()
  const emailId     = useId()
  const passwordId  = useId()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [busy,     setBusy]     = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setBusy(true)

    await new Promise(r => setTimeout(r, 400))

    if (isValidPractitionerLogin(email, password)) {
      setUserRole('practitioner')
      navigate('/before-we-begin')
      return
    }

    setError("We didn't recognise those details. Please try again.")
    setBusy(false)
  }

  const paletteVars = {
    '--lg-bg':   palette.bg,
    '--lg-text': palette.text,
    '--lg-ring': palette.ring,
  } as React.CSSProperties

  return (
    <main className={styles.page} style={paletteVars} aria-label="Practitioner sign in">
      <div className={styles.card}>

        <header className={styles.header}>
          <h1 className={styles.title}>Sign in</h1>
          <p className={styles.subtitle}>Practitioner access</p>
        </header>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Sign in form"
        >
          <div className={styles.field}>
            <label htmlFor={emailId} className={styles.label}>
              Email
            </label>
            <input
              id={emailId}
              type="email"
              className={styles.input}
              value={email}
              onChange={e => { setEmail(e.target.value); setError('') }}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              aria-required="true"
              aria-describedby={error ? 'login-error' : undefined}
              placeholder="your@email.com"
            />
          </div>

          <div className={styles.field}>
            <label htmlFor={passwordId} className={styles.label}>
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              className={styles.input}
              value={password}
              onChange={e => { setPassword(e.target.value); setError('') }}
              autoComplete="current-password"
              required
              aria-required="true"
              aria-describedby={error ? 'login-error' : undefined}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p id="login-error" className={styles.errorMsg} role="alert" aria-live="polite">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={busy || !email.trim() || !password}
            aria-disabled={busy || !email.trim() || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

      </div>
    </main>
  )
}
